import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select2 } from 'ng-select2-component';
import Swal from 'sweetalert2';
import { InstallationDataModel } from '../../../models/installation-data-model';
import { InstallationParams } from '../../../models/installation-params';
import { UserService } from '../../../services/user.service';

interface SelectItem {
  id: string;
  value: string;
  label: string;
  idRegion?: string;
  idSector?: string;
  idVnPlate?: string;
  idPvGroup?: string;
  idSectionPv?: string;
}

@Component({
  selector: 'app-distributor-common-filter',
  standalone: true,
  imports: [FormsModule, Select2],
  templateUrl: './distributor-common-filter.component.html',
  styleUrl: './distributor-common-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DistributorCommonFilterComponent implements OnInit, OnChanges {

  @Input() multiDr = false;
  @Input() multiSector = false;
  @Input() multiVnPlate = false;
  @Input() multiPvGroup = false;
  @Input() multiSectionPv = false;
  @Input() multiDistri = false;
  @Input() idBrand: number = 1;
  @Input() inputFilter: InstallationParams | null = null;

  @Input() hideDr = false;
  @Input() hideSector = false;
  @Input() hideVnPlate = false;
  @Input() hidePvGroup = false;
  @Input() hideSectionPv = true;
  @Input() hideDistri = false;
  @Input() hideResetFilter = false;

  @Input() requireDr = false;
  @Input() requireSector = false;
  @Input() requireVnPlate = false;
  @Input() requirePvGroup = false;
  @Input() requireSectionPv = false;
  @Input() requireDistri = false;
  
  @Input() showR2 = true;
  @Input() selectOnChange = false;
  @Input() variant: 'default' | 'topbar' = 'default';
  @Input() resettable = true;

  @HostBinding('class.is-topbar')
  get isTopbar(): boolean {
    return this.variant === 'topbar';
  }

  @Output() modelFilterEmit = new EventEmitter<InstallationParams>();
  @Output() modelInputFilterEmit = new EventEmitter<InstallationParams>();  
  @Output() resetEmit = new EventEmitter<InstallationParams>();

  modelFilter = new InstallationParams();
  InstallationParams = new InstallationParams();

  drSelected: string | string[] = '';
  drData: SelectItem[] = [];
  sectorSelected: string | string[] = '';
  sectorData: SelectItem[] = [];
  vnPlateSelected: string | string[] = '';
  vnPlateData: SelectItem[] = [];
  grupopvSelected: string | string[] = '';
  grouppvData: SelectItem[] = [];
  sectionpvSelected: string | string[] = '';
  sectionpvData: SelectItem[] = [];
  installationSelected: string | string[] = '';
  distributorData: SelectItem[] = [];

  private userDistributors: InstallationDataModel[] = [];
  private hasInputFilter = false;

  private userService = inject(UserService);
  private cd = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadUserDistributors();
  }

  ngOnChanges(changes: SimpleChanges): void {
    
    if (changes['idBrand']?.currentValue != null && changes['idBrand']?.previousValue != null) {
      this.InstallationParams = new InstallationParams();
      this.modelFilter = new InstallationParams();
      this.InstallationParams.IdBrand = +changes['idBrand'].currentValue;
      this.modelFilter.IdBrand = +changes['idBrand'].currentValue;
      this.loadDR();
    }

    if (changes['inputFilter']?.currentValue) {    
      this.hasInputFilter = true;
      this.inputFilter = changes['inputFilter'].currentValue;
      this.InstallationParams = { ...this.inputFilter! };
      this.modelFilter = { ...this.inputFilter! };
      this.loadDR();
      this.emitInputFilter();
    }
  }

  private loadUserDistributors(): void {
    this.userService.getUserInstallation().subscribe({
      next: resp => {
        this.userDistributors = this.showR2 ? resp : resp.filter(x => x.IdRelation === 1);

        if (this.inputFilter) {          
          this.hasInputFilter = true;
          this.InstallationParams = { ...this.inputFilter };
          this.modelFilter = { ...this.inputFilter };
          this.loadDR();
          this.emitInputFilter();
        } else {
          this.InstallationParams.IdBrand = +this.idBrand;
          this.modelFilter.IdBrand = +this.idBrand;
          this.loadDR();
        }
        this.cd.detectChanges();
      },
      error: () => this.showError('Error al recuperar los datos')
    });
  }

  private regionIdKey(): keyof InstallationDataModel {
    return this.idBrand === 2 ? 'IdRegionDacia' : 'IdRegionRenault';
  }
  private regionLabelKey(): keyof InstallationDataModel {
    return this.idBrand === 2 ? 'RegionDacia' : 'RegionRenault';
  }
  private sectorIdKey(): keyof InstallationDataModel {
    return this.idBrand === 2 ? 'IdSectorDacia' : 'IdSectorRenault';
  }
  private sectorLabelKey(): keyof InstallationDataModel {
    return this.idBrand === 2 ? 'SectorDacia' : 'SectorRenault';
  }

  private toArray(val: string | string[]): string[] {
    if (Array.isArray(val)) return val;
    return val ? [val] : [];
  }

  private autoSelect(data: SelectItem[], hide: boolean, multi: boolean, inputValues: string[]): string | string[] {    
    if (data.length === 1 && !hide)
      return multi ? [data[0].value] : data[0].value;
    const filter = this.hasInputFilter ? inputValues : [];
    return multi ? filter : (filter[0] ?? '');
  }

  loadDR(): void {
    const rId = this.regionIdKey();
    const rLabel = this.regionLabelKey();
    const unique = [...new Map(this.userDistributors.map(x => [x[rId], x])).values()];
    this.drData = unique
      .map(x => ({ id: String(x[rId]), value: String(x[rId]), label: String(x[rLabel]) }))
      .sort((a, b) => a.value.localeCompare(b.value));
    this.drSelected = this.autoSelect(this.drData, this.hideDr, this.multiDr, this.inputFilter?.Region ?? []);
    this.multiDr ? this.selectDR() : this.loadSector();
  }

  loadSector(): void {
    this.InstallationParams.Region = this.modelFilter.Region;
    const rId = this.regionIdKey(); const sId = this.sectorIdKey(); const sLabel = this.sectorLabelKey();
    let items = [...new Map(this.userDistributors.map(x => [x[sId], x])).values()]
      .map(x => ({ id: String(x[sId]), value: String(x[sId]), label: String(x[sLabel]), idRegion: String(x[rId]) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (this.InstallationParams.Region.length > 0)
      items = items.filter(x => this.InstallationParams.Region.includes(x.idRegion));
    this.sectorData = items;
    this.sectorSelected = this.autoSelect(this.sectorData, this.hideSector, this.multiSector, this.inputFilter?.Sector ?? []);
    this.multiSector ? this.selectSector() : this.loadVnPlate();
  }

  loadVnPlate(): void {
    this.InstallationParams.Region = this.modelFilter.Region;
    this.InstallationParams.Sector = this.modelFilter.Sector;
    const rId = this.regionIdKey(); const sId = this.sectorIdKey();
    let items = [...new Map(this.userDistributors.map(x => [x.IdVnPlate, x])).values()]
      .map(x => ({ id: String(x.IdVnPlate), value: String(x.IdVnPlate), label: x.VnPlate, idRegion: String(x[rId]), idSector: String(x[sId]) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (this.InstallationParams.Region.length > 0)
      items = items.filter(x => this.InstallationParams.Region.includes(x.idRegion));
    if (this.InstallationParams.Sector.length > 0)
      items = items.filter(x => this.InstallationParams.Sector.includes(x.idSector!));
    this.vnPlateData = items;
    this.vnPlateSelected = this.autoSelect(this.vnPlateData, this.hideVnPlate, this.multiVnPlate, this.inputFilter?.VnPlate ?? []);
    this.multiVnPlate ? this.selectVnPlate() : this.loadPvGroup();
  }

  loadPvGroup(): void {
    this.InstallationParams.Region = this.modelFilter.Region;
    this.InstallationParams.Sector = this.modelFilter.Sector;
    this.InstallationParams.VnPlate = this.modelFilter.VnPlate;
    const rId = this.regionIdKey(); const sId = this.sectorIdKey();
    let items = [...new Map(this.userDistributors.map(x => [x.IdPvGroup, x])).values()]
      .map(x => ({ id: String(x.IdPvGroup), value: String(x.IdPvGroup), label: x.PvGroup, idRegion: String(x[rId]), idSector: String(x[sId]), idVnPlate: String(x.IdVnPlate) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (this.InstallationParams.Region.length > 0)
      items = items.filter(x => this.InstallationParams.Region.includes(x.idRegion));
    if (this.InstallationParams.Sector.length > 0)
      items = items.filter(x => this.InstallationParams.Sector.includes(x.idSector!));
    if (this.InstallationParams.VnPlate.length > 0)
      items = items.filter(x => this.InstallationParams.VnPlate.includes(x.idVnPlate!));
    this.grouppvData = items;
    this.grupopvSelected = this.autoSelect(this.grouppvData, this.hidePvGroup, this.multiPvGroup, this.inputFilter?.PvGroup ?? []);
    this.multiPvGroup ? this.selectGroupPV() : this.loadSectionPv();
  }

  loadSectionPv(): void {
    this.InstallationParams.Region = this.modelFilter.Region;
    this.InstallationParams.Sector = this.modelFilter.Sector;
    this.InstallationParams.VnPlate = this.modelFilter.VnPlate;
    this.InstallationParams.PvGroup = this.modelFilter.PvGroup;
    const rId = this.regionIdKey(); const sId = this.sectorIdKey();
    let items = [...new Map(this.userDistributors.map(x => [x.SectionPv, x])).values()]
      .map(x => ({ id: x.SectionPv, value: x.SectionPv, label: x.SectionPvName, idRegion: String(x[rId]), idSector: String(x[sId]), idVnPlate: String(x.IdVnPlate), idPvGroup: String(x.IdPvGroup) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (this.InstallationParams.Region.length > 0)
      items = items.filter(x => this.InstallationParams.Region.includes(x.idRegion));
    if (this.InstallationParams.Sector.length > 0)
      items = items.filter(x => this.InstallationParams.Sector.includes(x.idSector!));
    if (this.InstallationParams.VnPlate.length > 0)
      items = items.filter(x => this.InstallationParams.VnPlate.includes(x.idVnPlate!));
    if (this.InstallationParams.PvGroup.length > 0)
      items = items.filter(x => this.InstallationParams.PvGroup.includes(x.idPvGroup!));
    this.sectionpvData = items;
    this.sectionpvSelected = this.autoSelect(this.sectionpvData, this.hideSectionPv, this.multiSectionPv, this.inputFilter?.SectionPv ?? []);
    this.multiSectionPv ? this.selectSectionPV() : this.loadInstallation();
  }

  loadInstallation(): void {
    this.InstallationParams.Region = this.modelFilter.Region;
    this.InstallationParams.Sector = this.modelFilter.Sector;
    this.InstallationParams.VnPlate = this.modelFilter.VnPlate;
    this.InstallationParams.PvGroup = this.modelFilter.PvGroup;
    this.InstallationParams.SectionPv = this.modelFilter.SectionPv;
    const rId = this.regionIdKey(); const sId = this.sectorIdKey();
    let items = this.userDistributors
      .map(x => ({ id: String(x.IdInstallation), value: String(x.IdInstallation), label: `${x.DealerId} - ${x.SocialName}`, idRegion: String(x[rId]), idSector: String(x[sId]), idVnPlate: String(x.IdVnPlate), idPvGroup: String(x.IdPvGroup), idSectionPv: x.SectionPv }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (this.InstallationParams.Region.length > 0)
      items = items.filter(x => this.InstallationParams.Region.includes(x.idRegion));
    if (this.InstallationParams.Sector.length > 0)
      items = items.filter(x => this.InstallationParams.Sector.includes(x.idSector!));
    if (this.InstallationParams.VnPlate.length > 0)
      items = items.filter(x => this.InstallationParams.VnPlate.includes(x.idVnPlate!));
    if (this.InstallationParams.PvGroup.length > 0)
      items = items.filter(x => this.InstallationParams.PvGroup.includes(x.idPvGroup!));
    if (this.InstallationParams.SectionPv.length > 0)
      items = items.filter(x => this.InstallationParams.SectionPv.includes(x.idSectionPv!));
    this.distributorData = items;
    
    this.installationSelected = this.autoSelect(this.distributorData, this.hideDistri, this.multiDistri, this.inputFilter?.Installation ?? []);
    
    if (this.multiDistri) this.selectInstallation();
    this.hasInputFilter = false;
  }

  selectDR(): void {
    this.modelFilter.Region = this.toArray(this.drSelected);
    if (this.selectOnChange) this.emitFilter();
    this.loadSector();
  }

  selectSector(): void {
    this.modelFilter.Sector = this.toArray(this.sectorSelected);
    if (this.selectOnChange) this.emitFilter();
    this.loadVnPlate();
  }

  selectVnPlate(): void {
    this.modelFilter.VnPlate = this.toArray(this.vnPlateSelected);
    if (this.selectOnChange) this.emitFilter();
    this.loadPvGroup();
  }

  selectGroupPV(): void {
    this.modelFilter.PvGroup = this.toArray(this.grupopvSelected);
    if (this.selectOnChange) this.emitFilter();
    this.loadSectionPv();
  }

  selectSectionPV(): void {
    this.modelFilter.SectionPv = this.toArray(this.sectionpvSelected);
    if (this.selectOnChange) this.emitFilter();
    this.loadInstallation();
  }

  selectInstallation(): void {
    this.modelFilter.Installation = this.toArray(this.installationSelected);
    if (this.selectOnChange) this.emitFilter();
  }

  emitFilter(): void { this.modelFilterEmit.emit(this.modelFilter); }
  emitInputFilter(): void { this.modelInputFilterEmit.emit(this.modelFilter); }

  emitReset(): void {
    this.modelFilter = new InstallationParams();
    this.drSelected = this.multiDr ? [] : '';
    this.sectorSelected = this.multiSector ? [] : '';
    this.vnPlateSelected = this.multiVnPlate ? [] : '';
    this.grupopvSelected = this.multiPvGroup ? [] : '';
    this.sectionpvSelected = this.multiSectionPv ? [] : '';
    this.installationSelected = this.multiDistri ? [] : '';
    this.resetEmit.emit(this.modelFilter);
    this.loadDR();
  }

  private showError(title: string): void {
    Swal.fire({
      icon: 'error',
      title,
      text: 'Ocurrió un error inesperado.',
      buttonsStyling: false,
      showCancelButton: false,
      showDenyButton: false,
      confirmButtonText: 'Aceptar',
      customClass: {
        actions: 'swal-actions-custom',
        confirmButton: 'btn-red',
      },
    });
  }
}
